import React from "react";
import profileImage from "./images/profile.jpeg";
import heroBackgroundImage from "./images/background.png";
import TechCarousel from './components/TechCarousel';

function App() {
  return (
    <div className="font-sans text-gray-800">

      {/* Hero Section */}
      <section
        className="text-white py-20 px-4 relative bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBackgroundImage})` }}
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center md:items-start justify-between relative z-10">
          <div className="md:w-2/3 text-center md:text-left mb-8 md:mb-0">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Full Stack Developer
            </h1>
            <p className="text-lg md:text-xl mb-2">
              Available for challenging roles across Europe 🌍, open to relocation and collaboration. 
            </p>
            <p className="text-lg md:text-xl">
              Dedicated to building impactful software with a strong foundation in front-end and back-end development. 🚀
            </p>
          </div>
          <div className="md:w-1/3 flex justify-center md:justify-end">
            <img
              src={profileImage}
              alt="Profile"
              className="w-40 h-40 md:w-48 md:h-48 rounded-xl object-cover shadow-2xl border-4 border-white"
            />
          </div>
        </div>

        {/* Gradient overlay for smooth transition */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* About Me Section */}
      <section className="max-w-6xl mx-auto px-4 -mt-10 relative z-10">
        <div className="bg-white shadow-xl rounded-3xl p-10 md:p-16 text-gray-800">
          <h2 className="text-3xl font-semibold mb-6">About Me</h2>
          <p className="text-lg leading-relaxed mb-4">
            A dedicated Junior Software Developer with solid experience in both front-end and back-end development, having worked on significant projects such as <strong>YVR</strong>, <strong>Schnitzel Welt</strong> and <strong>Trip App</strong>, utilizing technologies like <strong>C#</strong>, <strong>Angular</strong>, <strong>React</strong>, and <strong>Blazor</strong>.
          </p>
          <p className="text-lg leading-relaxed mb-4">
            Holder of a Bachelor's degree in Software and Data Engineering, along with certifications from multiple industry-recognized courses. Passionate about continuous learning and contributing to impactful software solutions.
          </p>
          <p className="text-lg leading-relaxed">
            Successfully contributed to the development and optimization of YVR, Trip App and Schnitzel Welt gaining valuable experience across various software architectures and modern technologies.
          </p>
        </div>
      </section>

      {/* Projects Section */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-semibold mb-10 text-center">My Projects</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* YVR Project */}
          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200">
            <h3 className="text-2xl font-bold mb-3 text-blue-600">✈️ YVR Flight Tracker</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Developed for the Canadian market, this app tracks real-time flight takeoffs, landings, and delays at YVR Airport.
              I contributed to both frontend and backend, with focus on optimization for Android and desktop users using MVC architecture.
            </p>
            <ul className="text-sm text-gray-600 mb-4 list-disc list-inside space-y-1">
              <li><strong>Front-End:</strong> JavaScript, jQuery, HTML, CSS</li>
              <li><strong>Back-End:</strong> C#, MySQL (stored procedures)</li>
            </ul>
            <a
              href="https://ais.yvr.ca/identity/Login?ReturnUrl=%2Farrivals%2Ftoday"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline font-medium"
            >
              Visit YVR App
            </a>
          </div>

          {/* TripApp Project */}
          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200">
            <h3 className="text-2xl font-bold mb-3 text-green-700">🌍 TripApp (Travel Agency)</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Full-stack development of TripApp — a modern solution for sub-agencies to manage and sell travel arrangements in Serbia.
              Worked across Clean Architecture and MVC patterns with real API integration and user role management.
            </p>
            <ul className="text-sm text-gray-600 mb-4 list-disc list-inside space-y-1">
              <li><strong>Front-End:</strong> React, Next.js, Blazor</li>
              <li><strong>Back-End:</strong> C#, Clean Architecture, DTO Mapping</li>
              <li><strong>Features:</strong> CRUD, QR Generator, PDF (QuestPDF), File Uploads, Admin Panel</li>
            </ul>
            <a
              href="https://tripap.rs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 underline font-medium"
            >
              Visit TripApp
            </a>
          </div>

          {/* Schnitzel welt App Project */}
          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200">
            <h3 className="text-2xl font-bold mb-3 text-red-600">🍔 Schnitzel Welt (Food App)</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Developed a comprehensive food ordering platform for restaurants in Vienna, Austria. 
              Focused on optimizing performance and seamless API integration for real-time menu updates and order processing.
            </p>
            <ul className="text-sm text-gray-600 mb-4 list-disc list-inside space-y-1">
              <li><strong>Front-End:</strong> Angular, HTML, CSS, JavaScript – optimized UI rendering and dynamic component generation.</li>
              <li><strong>Back-End:</strong> C# – implemented service logic, API communication, JSON parsing, and integrated cash register systems.</li>
            </ul>
            <a
              href="https://www.schnitzelwelt.at/home"
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-600 underline font-medium"
            >
              Visit Schnitzel Welt
            </a>
          </div>

        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-blue-50 via-white to-purple-50 rounded-3xl shadow-xl p-10">
          <h2 className="text-3xl font-semibold text-center mb-10">Technologies I Use</h2>
          <TechCarousel />
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-semibold mb-4">Contact</h2>
        <p className="text-lg mb-4">
          Feel free to reach out at:{" "}
          <a href="mailto:aleksa.grbic007@gmail.com" className="text-blue-600 underline">
            aleksa.grbic007@gmail.com
          </a>
        </p>
        <div className="flex justify-center space-x-6">
          {/* GitHub icon */}
          <a href="https://github.com/Lexaa00" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-black">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577v-2.234c-3.338.726-4.033-1.415-4.033-1.415-.546-1.387-1.333-1.757-1.333-1.757-1.09-.745.083-.73.083-.73 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.807 1.305 3.492.997.108-.775.418-1.305.76-1.605-2.665-.304-5.466-1.335-5.466-5.933 0-1.31.47-2.382 1.236-3.222-.124-.303-.536-1.523.117-3.176 0 0 1.008-.322 3.3 1.23a11.5 11.5 0 013.003-.404c1.018.005 2.045.138 3.003.404 2.29-1.553 3.296-1.23 3.296-1.23.655 1.653.243 2.873.12 3.176.77.84 1.235 1.913 1.235 3.222 0 4.61-2.807 5.625-5.48 5.922.43.37.823 1.1.823 2.22v3.293c0 .322.218.697.825.577C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z" />
            </svg>
          </a>

          {/* LinkedIn icon */}
          <a href="https://www.linkedin.com/in/aleksa-grbi%C4%87-369790242/" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-900">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.327-.026-3.037-1.852-3.037-1.853 0-2.136 1.447-2.136 2.942v5.664H9.352V9h3.414v1.561h.049c.476-.9 1.637-1.852 3.367-1.852 3.6 0 4.268 2.37 4.268 5.455v6.288zM5.337 7.433a2.065 2.065 0 110-4.13 2.065 2.065 0 010 4.13zM6.814 20.452H3.861V9h2.953v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.226.792 24 1.771 24h20.451c.979 0 1.778-.774 1.778-1.729V1.729C24 .774 23.204 0 22.225 0z" />
            </svg>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white text-center py-6 mt-10">
        <p>&copy; 2025 My Portfolio. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;